import { useState, useEffect } from 'react';
import {
  Input,
  Select,
  Tag,
  DatePicker,
  Button,
  Form,
  Upload,
  Row,
  Col,
  InputNumber,
  Flex
} from 'antd';
import {
  PlusOutlined,
  CameraOutlined,
  CloseOutlined
} from '@ant-design/icons/lib';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useEnumsLoader } from '../../utils/useEnumsLoader';
import { CustomerType } from '../../utils/constants/tagColors';

const { Option } = Select;
const { Dragger } = Upload;

/**
 * Hook personnalisé pour générer les champs d'un formulaire de facture.
 * @param {Object} options - Les options pour le hook.
 * @param {Array} options.filesList - La liste des fichiers téléchargés.
 * @param {function} options.setFilesList - La fonction pour mettre à jour la liste des fichiers.
 * @param {Array} options.customers - La liste des clients (uniquement pour le type de facture "customer").
 * @param {function} options.setWordings - La fonction pour mettre à jour les libellés.
 * @param {Array} options.wordings - Les libellés.
 * @param {function} options.setCustomer - La fonction pour définir le client sélectionné.
 * @param {Array} options.selectedSupervisions - Les supervisions sélectionnées (uniquement pour le type de facture "customer").
 * @param {function} options.setSupervision - La fonction pour mettre à jour les supervisions.
 * @param {function} options.setWordingTotalHT - La fonction pour mettre à jour le total HT d'un libellé (uniquement pour le type de facture "customer").
 * @param {function} options.setWordingTotalTVA - La fonction pour mettre à jour le total TVA d'un libellé.
 * @param {function} options.setWordingTotalReduction - La fonction pour mettre à jour le total de réduction d'un libellé (uniquement pour le type de facture "customer").
 * @param {function} options.setWordingUnitPrice - La fonction pour mettre à jour le prix unitaire d'un libellé (uniquement pour le type de facture "intern").
 * @param {function} options.setWordingQuantity - La fonction pour mettre à jour la quantité d'un libellé (uniquement pour le type de facture "intern").
 * @param {string} options.invoiceType - Le type de facture ("customer" ou "intern").
 * @param {function} options.setPaymentMethod - La fonction pour mettre à jour la méthode de paiement.
 * @param {string} options.paymentMethod - La méthode de paiement sélectionnée.
 * @returns {Object} Les champs de formulaire et leur état de chargement.
 */

export const useFields = ({
  filesList,
  setFilesList,
  customers,
  setCustomer,
  selectedSupervisions,
  setSupervision,
  invoiceType,
  setPaymentMethod,
  paymentMethod,
  setUpdatedWordingType,
  setUpdateWordingFields,
  updateWordingFields,
  form
}) => {
  const { t } = useTranslation();
  const { enums, isFieldsLoading } = useEnumsLoader('/customerinvoices/enums');
  const [selectedType, setSelectedType] = useState(null);
  const [fieldNumber, setFieldNumber] = useState(0);
  const [disabledPaymentMethod, setDisabledPaymentMethod] = useState(false);
  const [isUpdatingTotals, setIsUpdatingTotals] = useState(false);
  const [tvaValue, setTvaValue] = useState(null);

  const calculateAverageTVA = () => {
    const tva = form.getFieldValue('tva') || 10;

    return tva;
  };

  const calculateTTCFromHT = (value) => {
    const tvaRate = calculateAverageTVA();
    return value * (1 + tvaRate / 100);
  };

  const calculateHTFromTTC = (value) => {
    const tvaRate = calculateAverageTVA();
    return value / (1 + tvaRate / 100);
  };

  const draggerProps = () => ({
    onRemove: (file) => {
      setFilesList(filesList.filter((f) => f.uid !== file.uid));
    },
    beforeUpload: (file) => {
      setFilesList((prevFileList) => [...prevFileList, file]);

      return false;
    },
    fileList: filesList || []
  });

  const filterOption = (input, option) => {
    const children = option?.props?.customer;
    if (children) {
      const { first_name, last_name, reference } = children;

      return (
        String(first_name).toLowerCase().includes(input.toLowerCase()) ||
        String(last_name).toLowerCase().includes(input.toLowerCase()) ||
        String(reference).toLowerCase().includes(input.toLowerCase())
      );
    }

    return false;
  };

  useEffect(() => {
    const status = form.getFieldValue('status');
    if (status === 'FREE') form.setFieldsValue({ payment_method: 'FREE' });

    // Initialiser la valeur TVA si elle existe déjà
    const existingTva = form.getFieldValue('tva');
    if (existingTva) setTvaValue(existingTva);
  }, [form]);

  const handleRemoveWording = (name, remove) => {
    remove(name);
    setFieldNumber((prevFieldNumber) => prevFieldNumber - 1);
    setUpdateWordingFields(!updateWordingFields);
  };

  const invoiceFields = [
    ...(invoiceType === 'customer'
      ? [
          {
            name: ['type'],
            rules: [{ required: true }],
            input: (
              <Select
                loading={isFieldsLoading}
                onChange={(e) => setSelectedType(e)}
              >
                {(enums?.type || [])
                  .filter((el) => el !== 'CREDIT_NOTE')
                  .map((type) => (
                    <Option key={type} value={type}>
                      <Tag>{t(`customerinvoices.tags.${type}`)}</Tag>
                    </Option>
                  ))}
              </Select>
            )
          },
          {
            name: ['customer'],
            rules: [{ required: true }],
            input: (
              <Select
                showSearch
                loading={isFieldsLoading}
                filterOption={filterOption}
                onChange={(e, option) => {
                  setCustomer(option.customer);
                  // The supervision chosen belongs to the previous customer
                  form.resetFields(['supervision']);
                }}
              >
                {(customers || []).map((customer) => (
                  <Option
                    key={customer?.user?._id}
                    value={customer?.user?._id}
                    customer_type={customer.customer_type}
                    customer={customer}
                  >
                    <Row justify="space-around" align="center">
                      <Col>{`${customer.first_name} ${customer.last_name}`}</Col>
                      <Col>
                        <Tag color={CustomerType[customer.customer_type]}>
                          {t(`customerinvoices.tags.${customer.customer_type}`)}
                        </Tag>
                      </Col>
                    </Row>
                  </Option>
                ))}
              </Select>
            )
          },
          {
            name: ['supervision'],
            hidden: selectedType === 'SUBSCRIPTION',
            input: (
              <Select
                showSearch
                filterOption={(input, option) => {
                  const children = option?.props?.supervision;
                  if (children) {
                    const { reference } = children;

                    return String(reference)
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }

                  return false;
                }}
                loading={isFieldsLoading}
                onChange={(_, option) => setSupervision(option.supervision)}
              >
                {(selectedSupervisions || []).map((supervision) => (
                  <Option
                    key={supervision._id}
                    value={supervision._id}
                    supervision={supervision}
                  >
                    {supervision.reference}
                  </Option>
                ))}
              </Select>
            )
          }
        ]
      : []),
    ...(invoiceType === 'intern'
      ? [
          {
            name: ['emitter']
          },
          {
            name: ['recipient_name']
          }
        ]
      : []),
    {
      name: ['payment_method'],
      input: (
        <Select
          loading={isFieldsLoading}
          onChange={(e) => setPaymentMethod(e)}
          allowClear
          disabled={disabledPaymentMethod}
        >
          {(enums?.payment_method || []).map((payment_method) => (
            <Option key={payment_method} value={payment_method}>
              {t(
                `customerinvoices.tags.${
                  payment_method === 'CHEQUE' ? 'CHEQUE_METHOD' : payment_method
                }`
              )}
            </Option>
          ))}
        </Select>
      ),
      dependencies: ['status'],
      rules: [
        ({ getFieldValue }) => ({
          validator(_, value) {
            const status = getFieldValue('status');
            if (!status) {
              return Promise.reject(new Error('Entrez un statut'));
            }
            // A paid invoice prints its payment mention, so the method has to
            // be captured here as the "paid" modal already requires it.
            if (status === 'PAID' && !value) {
              return Promise.reject(
                new Error(t('customerinvoices.form.payment_method_required'))
              );
            }
            return Promise.resolve();
          }
        })
      ]
    },
    ...(paymentMethod === 'CHEQUE' ? [{ name: ['cheque_number'] }] : []),
    {
      name: ['invoicing_address']
    },
    {
      name: ['postal_code']
    },
    {
      name: ['city']
    },
    ...(invoiceType === 'intern'
      ? [
          {
            name: ['wording'],
            rules: [{ required: true }]
          }
        ]
      : []),
    {
      name: ['emission_date'],
      input: <DatePicker format="DD/MM/YYYY" />
    },
    {
      name: ['payment_date'],
      input: <DatePicker format="DD/MM/YYYY" />
    },
    ...(selectedType !== 'SUBSCRIPTION'
      ? [
          {
            name: ['due_date'],
            input: <DatePicker format="DD/MM/YYYY" />
          }
        ]
      : []),
    {
      name: ['status'],
      rules: [{ required: true }],
      input: (
        <Select
          loading={isFieldsLoading}
          onChange={(value) => {
            if (value === 'FREE') {
              form.setFieldValue('payment_method', 'FREE');
            } else if (value !== 'PAID') {
              // Switching to PAID keeps the method chosen above: wiping it
              // here is what produced paid invoices without any method.
              form.setFieldValue('payment_method', undefined);
            }
            if (value !== 'PAID') {
              setDisabledPaymentMethod(true);
            } else {
              setDisabledPaymentMethod(false);
            }
            if (value === 'PAID') {
              if (!form.getFieldValue('payment_date')) {
                form.setFieldValue('payment_date', dayjs());
              }
            } else {
              form.setFieldValue('payment_date', null);
            }
          }}
        >
          {(enums?.status || []).map((status) => (
            <Option key={status} value={status}>
              {t(`customerinvoices.tags.${status}`)}
            </Option>
          ))}
        </Select>
      )
    },
    ...(invoiceType === 'customer'
      ? [
          {
            noLabel: true,
            input: (
              <Form.List name={['wordings']}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ name, key }) => (
                      <Flex
                        vertical
                        gap="small"
                        key={key}
                        className="invoice-line"
                        wrap="wrap"
                      >
                        <Button
                          style={{ alignSelf: 'end' }}
                          type="link"
                          onClick={() => handleRemoveWording(name, remove)}
                        >
                          <CloseOutlined />
                        </Button>
                        {t('interninvoices.form.wordings.title')}
                        <Form.Item name={[name, 'title']} noStyle>
                          <Input />
                        </Form.Item>
                        {invoiceType === 'customer' && (
                          <>
                            {t('interninvoices.form.wordings.total_ht')}
                            <Form.Item name={[name, 'total_ht']} noStyle>
                              <InputNumber
                                onChange={() => {
                                  setUpdatedWordingType('HT');
                                  setUpdateWordingFields(!updateWordingFields);
                                }}
                              />
                            </Form.Item>
                          </>
                        )}
                        {invoiceType === 'intern' && (
                          <>
                            {t('interninvoices.form.wordings.unit_price')}
                            <Form.Item name={[name, 'unit_price']} noStyle>
                              <InputNumber
                                onChange={() =>
                                  setUpdateWordingFields(!updateWordingFields)
                                }
                              />
                            </Form.Item>
                            {t('interninvoices.form.wordings.quantity')}
                            <Form.Item name={[name, 'quantity']} noStyle>
                              <InputNumber
                                onChange={() =>
                                  setUpdateWordingFields(!updateWordingFields)
                                }
                              />
                            </Form.Item>
                          </>
                        )}
                        {t('interninvoices.form.wordings.tva')}
                        <Form.Item name={[name, 'tva']} noStyle>
                          <InputNumber
                            onChange={() =>
                              setUpdateWordingFields(!updateWordingFields)
                            }
                          />
                        </Form.Item>
                        {invoiceType === 'customer' && (
                          <>
                            {t('interninvoices.form.wordings.reduction')}
                            <Form.Item name={[name, 'reduction']} noStyle>
                              <InputNumber
                                onChange={() => {
                                  setUpdatedWordingType('REDUCTION');
                                  setUpdateWordingFields(!updateWordingFields);
                                }}
                              />
                            </Form.Item>
                          </>
                        )}
                        {t('interninvoices.form.wordings.total_ttc')}
                        <Form.Item name={[name, 'total_ttc']} noStyle>
                          <InputNumber
                            onChange={() => {
                              setUpdatedWordingType('TTC');
                              setUpdateWordingFields(!updateWordingFields);
                            }}
                          />
                        </Form.Item>
                      </Flex>
                    ))}
                    <Button
                      type="dashed"
                      block
                      onClick={() => {
                        add();
                        setFieldNumber(
                          (prevFieldNumber) => prevFieldNumber + 1
                        );
                        form.setFieldValue(
                          ['wordings', fieldNumber, 'tva'],
                          10
                        );
                      }}
                      icon={<PlusOutlined />}
                    >
                      {t('interninvoices.form.wordings_add_button')}
                    </Button>
                  </>
                )}
              </Form.List>
            )
          }
        ]
      : []),
    ...(invoiceType === 'intern'
      ? [
          {
            name: ['tva'],
            input: (
              <InputNumber
                onChange={(value) => {
                  form.setFieldsValue({
                    total_ttc: null,
                    total_ht: null,
                    tva: value
                  });
                  setTvaValue(value);
                }}
              />
            ),
            rules: [{ required: true }]
          }
        ]
      : []),
    {
      name: ['total_ht'],
      input: (
        <InputNumber
          disabled={
            invoiceType === 'customer' ||
            (invoiceType === 'intern' && !tvaValue)
          }
          onChange={(value) => {
            if (value && !isUpdatingTotals) {
              setIsUpdatingTotals(true);
              const ttcValue = calculateTTCFromHT(value);
              form.setFieldValue(
                'total_ttc',
                Number.parseFloat(ttcValue.toFixed(2))
              );
              setTimeout(() => setIsUpdatingTotals(false), 0);
            }
          }}
        />
      ),
      rules: [
        { required: true },
        ({ getFieldValue }) => ({
          validator() {
            if (invoiceType === 'intern' && !getFieldValue('tva')) {
              return Promise.reject(
                new Error("Veuillez d'abord saisir le taux de TVA")
              );
            }
            return Promise.resolve();
          }
        })
      ]
    },
    ...(invoiceType === 'customer'
      ? [
          {
            name: ['total_reduction_ttc'],
            input: <InputNumber disabled />
          }
        ]
      : []),
    {
      name: ['total_ttc'],
      input: (
        <InputNumber
          disabled={
            invoiceType === 'customer' ||
            (invoiceType === 'intern' && !tvaValue)
          }
          onChange={(value) => {
            if (value && !isUpdatingTotals) {
              setIsUpdatingTotals(true);
              const htValue = calculateHTFromTTC(value);
              form.setFieldValue(
                'total_ht',
                Number.parseFloat(htValue.toFixed(2))
              );
              setTimeout(() => setIsUpdatingTotals(false), 0);
            }
          }}
        />
      ),
      rules: [
        { required: true },
        ({ getFieldValue }) => ({
          validator() {
            if (invoiceType === 'intern' && !getFieldValue('tva')) {
              return Promise.reject(
                new Error("Veuillez d'abord saisir le taux de TVA")
              );
            }
            return Promise.resolve();
          }
        })
      ]
    },
    {
      name: ['files'],
      input: (
        <Dragger {...draggerProps()}>
          <p className="ant-upload-drag-icon">
            <CameraOutlined style={{ color: 'var(--textColor)' }} />
          </p>
          <p className="ant-upload-text">{t('files.create.action')}</p>
        </Dragger>
      )
    }
  ];

  return {
    invoiceFields,
    isFieldsLoading
  };
};
